import type { BreakpointState } from '@angular/cdk/layout';
import { BreakpointObserver } from '@angular/cdk/layout';
import { inject, Injectable } from '@angular/core';
import { SpriConfigService } from '@spri/services/config';
import { fromPairs } from 'lodash-es';
import type { Observable } from 'rxjs';
import { map, ReplaySubject, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SpriMediaWatcherService {
  private breakpointObserver = inject(BreakpointObserver);
  private spriConfigService = inject(SpriConfigService);

  private onMediaChange: ReplaySubject<{
    matchingAliases: string[];
    matchingQueries: Record<string, string>;
  }> = new ReplaySubject<{
    matchingAliases: string[];
    matchingQueries: Record<string, string>;
  }>(1);

  /**
   * Constructor
   */
  constructor() {
    this.spriConfigService.config$
      .pipe(
        map((config) =>
          fromPairs(Object.entries(config.screens).map(([alias, screen]) => [alias, `(min-width: ${screen})`]))
        ),
        switchMap((screens) =>
          this.breakpointObserver.observe(Object.values(screens)).pipe(
            map((state) => {
              // Prepare the observable values and set their defaults
              const matchingAliases: string[] = [];
              const matchingQueries: Record<string, string> = {};

              // Get the matching breakpoints and use them to fill the subject
              const matchingBreakpoints = Object.entries(state.breakpoints).filter(([, matches]) => matches) ?? [];
              for (const [query] of matchingBreakpoints) {
                // Find the alias of the matching query
                const found = Object.entries(screens).find(([, q]) => q === query);
                const matchingAlias = found ? found[0] : undefined;

                // Add the matching query to the observable values
                if (matchingAlias) {
                  matchingAliases.push(matchingAlias);
                  matchingQueries[matchingAlias] = query;
                }
              }

              // Execute the observable
              this.onMediaChange.next({
                matchingAliases,
                matchingQueries,
              });
            })
          )
        )
      )
      .subscribe();
  }

  public readonly onMediaChange$: Observable<{
    matchingAliases: string[];
    matchingQueries: Record<string, string>;
  }> = this.onMediaChange.asObservable();

  /**
   * On media query change
   *
   * @param query
   */
  public onMediaQueryChange$(query: string | string[]): Observable<BreakpointState> {
    return this.breakpointObserver.observe(query);
  }
}
